import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  SecurityRounded as SecurityIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { usePasskey } from '../contexts/PasskeyContext';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { EncryptionService } from '../services/encryptionService';

const TEST_VALUE = 'mythoughts.buzz';

export const PasskeyPrompt: React.FC = () => {
  const { setPasskey } = usePasskey();
  const { currentUser } = useAuth();
  const [passkeyInput, setPasskeyInput] = useState('');
  const [confirmPasskeyInput, setConfirmPasskeyInput] = useState('');
  const [showPasskey, setShowPasskey] = useState(false);
  const [showConfirmPasskey, setShowConfirmPasskey] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingUser, setCheckingUser] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);

  // Check if user is new (no encrypted test data)
  useEffect(() => {
    const checkUserStatus = async () => {
      if (!currentUser) {
        setCheckingUser(false);
        return;
      }

      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists() && userDoc.data()?.encryptedTest) {
          // Existing user - has passkey set up
          setIsNewUser(false);
        } else {
          // New user - needs to set up passkey
          setIsNewUser(true);
        }
      } catch (err) {
        console.error('Error checking user status:', err);
      } finally {
        setCheckingUser(false);
      }
    };

    checkUserStatus();
  }, [currentUser]);

  const handleSubmit = async () => {
    setError('');

    if (!passkeyInput.trim()) {
      setError('Please enter a passkey');
      return;
    }

    if (passkeyInput.length < 4) {
      setError('Passkey must be at least 4 characters');
      return;
    }

    // For new users, validate confirmation
    if (isNewUser) {
      if (!confirmPasskeyInput.trim()) {
        setError('Please confirm your passkey');
        return;
      }

      if (passkeyInput !== confirmPasskeyInput) {
        setError('Passkeys do not match');
        return;
      }
    }

    if (!currentUser) {
      setError('User not authenticated');
      return;
    }

    try {
      setLoading(true);

      const userDocRef = doc(db, 'users', currentUser.uid);

      if (isNewUser) {
        // First time - create encrypted test value
        const encryptedTest = EncryptionService.encrypt(TEST_VALUE, passkeyInput);
        await setDoc(
          userDocRef,
          {
            encryptedTest,
            createdAt: new Date(),
            updatedAt: new Date(),
          },
          { merge: true }
        );

        // Set passkey
        setPasskey(passkeyInput);
      } else {
        // Existing user - verify passkey
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists() && userDoc.data()?.encryptedTest) {
          try {
            const decrypted = EncryptionService.decrypt(
              userDoc.data().encryptedTest,
              passkeyInput
            );

            if (decrypted === TEST_VALUE) {
              // Passkey is correct
              setPasskey(passkeyInput);
              return;
            } else {
              setError('Invalid passkey. Please try again.');
              return;
            }
          } catch (err) {
            setError('Invalid passkey. Please try again.');
            return;
          }
        }
      }
    } catch (err) {
      console.error('Error verifying passkey:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checkingUser) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // New User UI - First time setup
  if (isNewUser) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <SecurityIcon sx={{ fontSize: 60, color: 'primary.main' }} />
            </Box>

            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              align="center"
              sx={{ fontWeight: 600 }}
            >
              Create Your Passkey
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              paragraph
              align="center"
              sx={{ mb: 3 }}
            >
              Welcome! Set up a passkey to encrypt and protect all your thoughts, diary entries, and journals.
            </Typography>

            {/* Warning Alert */}
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              sx={{ 
                mb: 3,
                '& .MuiAlert-message': { width: '100%' }
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                ⚠️ Important - Please Read Carefully
              </Typography>
              <Typography variant="body2">
                • <strong>If you lose your passkey, you lose all your data.</strong> There is no recovery option.
                <br />
                • Your thoughts, diary entries, and journals are encrypted with this passkey.
                <br />
                • We cannot recover your data without your passkey.
                <br />
                • You can change your passkey later in Settings.
              </Typography>
            </Alert>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              type="text"
              label="Create Passkey"
              value={passkeyInput}
              onChange={(e) => setPasskeyInput(e.target.value)}
              autoFocus
              sx={{ mb: 2 }}
              helperText="Minimum 4 characters. Choose something memorable!"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              type="text"
              label="Confirm Passkey"
              value={confirmPasskeyInput}
              onChange={(e) => setConfirmPasskeyInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit();
                }
              }}
              sx={{ mb: 3 }}
              error={confirmPasskeyInput.length > 0 && passkeyInput !== confirmPasskeyInput}
              helperText={
                confirmPasskeyInput.length > 0 && passkeyInput !== confirmPasskeyInput
                  ? 'Passkeys do not match'
                  : 'Re-enter your passkey to confirm'
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading || !passkeyInput || !confirmPasskeyInput || passkeyInput !== confirmPasskeyInput}
              sx={{ py: 1.5 }}
            >
              {loading ? 'Creating...' : 'Create Passkey & Continue'}
            </Button>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                🔒 <strong>Your privacy is guaranteed:</strong>
                <br />• Passkey is stored only on your device
                <br />• All data encrypted with AES-256
                <br />• Server only stores encrypted data
                <br />• No one can read your data without your passkey
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Existing User UI - Sign in with passkey
  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <SecurityIcon sx={{ fontSize: 60, color: 'primary.main' }} />
          </Box>

          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            align="center"
            sx={{ fontWeight: 600 }}
          >
            Enter Your Passkey
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            paragraph
            align="center"
            sx={{ mb: 4 }}
          >
            Enter your passkey to decrypt and access your thoughts.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            type={showPasskey ? 'text' : 'password'}
            label="Passkey"
            value={passkeyInput}
            onChange={(e) => setPasskeyInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
            autoFocus
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPasskey(!showPasskey)} edge="end">
                    {showPasskey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ py: 1.5 }}
          >
            {loading ? 'Verifying...' : 'Unlock'}
          </Button>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              🔒 <strong>Forgot your passkey?</strong>
              <br />Unfortunately, we cannot recover your data without your passkey.
              Your data is encrypted and only accessible with the correct passkey.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default PasskeyPrompt;
